import { Platform, Pressable, View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';

import { Text } from '@/ui';

type PillTab<T extends string> = {
  id: T;
  label: string;
};

type PillTabsProps<T extends string> = {
  tabs: readonly PillTab<T>[];
  activeId: T;
  onChange: (id: T) => void;
};

export function PillTabs<T extends string>({
  tabs,
  activeId,
  onChange,
}: PillTabsProps<T>) {
  const { styles } = useStyles(stylesheet);

  return (
    <View style={styles.row}>
      {tabs.map((tab) => {
        const isActive = tab.id === activeId;

        return (
          <Pressable
            key={tab.id}
            onPress={(event) => {
              onChange(tab.id);
              if (Platform.OS === 'web') {
                (
                  event.currentTarget as unknown as { blur?: () => void }
                ).blur?.();
              }
            }}
            style={isActive ? styles.tabActive : styles.tab}
          >
            <Text
              type="bodyRegular"
              style={isActive ? styles.labelActive : styles.label}
            >
              {tab.label}
            </Text>
          </Pressable>
        );
      })}
    </View>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  tab: {
    height: 40,
    borderRadius: 100,
    paddingHorizontal: 16,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.colors.button.secondary.default.fill,
    borderWidth: 1,
    borderColor: theme.colors.button.secondary.default.border,
    outlineWidth: 0,
  },
  tabActive: {
    height: 40,
    borderRadius: 100,
    paddingHorizontal: 16,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.colors.button.primary.default.fill,
    outlineWidth: 0,
  },
  label: {
    color: theme.colors.button.secondary.default.text,
  },
  labelActive: {
    color: theme.colors.button.primary.default.text,
  },
}));
